import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, EmptyState, ConfirmModal } from '../components/common';
import { TopicModal } from '../components/modals';
import TopicDiscussion from '../components/TopicDiscussion';
import { formatDate, getAgeDays } from '../utils/formatters';

/**
 * Active Meeting Session page - Real-time meeting topic discussion
 */
const ActiveMeetingSession = ({ session, meeting, user, onEndMeeting }) => {
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [users, setUsers] = useState([]);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('dateAdded');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [confirmState, setConfirmState] = useState(null);
  const [closeTopicError, setCloseTopicError] = useState('');

  useEffect(() => {
    // Load data immediately
    loadData();
    
    // Poll every 5 seconds to keep topics updated during active meeting
    const pollInterval = setInterval(() => {
      loadData();
    }, 5000);
    
    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  }, [meeting.id]);

  const loadData = async () => {
    try {
      const [topicsData, usersData] = await Promise.all([
        api.get(`/topics/meeting/${meeting.id}`),
        api.get('/users/list')
      ]);
      setTopics(topicsData);
      // Filter users to only meeting participants
      const meetingUsers = usersData.filter(u => meeting.participants.includes(u.id));
      setUsers(meetingUsers);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTopic = async () => {
    if (!selectedTopic) return;
    try {
      await api.patch(`/topics/${selectedTopic.id}/close`, { sessionId: session.id });
      setSelectedTopic(null);
      loadData();
    } catch (error) {
      setSelectedTopic(null);
      setCloseTopicError(error.message || 'Failed to close topic');
    }
  };

  const handleEndMeeting = () => {
    setConfirmState({
      title: 'End Meeting',
      message: 'Are you sure you want to end this meeting?',
      confirmLabel: 'End Meeting',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          const result = await api.patch(`/sessions/${session.id}/end`);
          onEndMeeting({ ...session, ...result });
        } catch (error) {
          console.error('Failed to end meeting:', error);
        }
      },
    });
  };

  // Calculate age and sort topics
  const sortedTopics = useMemo(() => {
    const openTopics = topics.filter(t => !t.closed);

    // Add age to topics
    const topicsWithAge = openTopics.map(topic => ({
      ...topic,
      ageInDays: getAgeDays(topic.createdAt)
    }));

    // Sort based on sortBy
    return [...topicsWithAge].sort((a, b) => {
      if (sortBy === 'dateAdded') {
        return new Date(b.createdAt) - new Date(a.createdAt); // Newest first
      } else if (sortBy === 'age') {
        return b.ageInDays - a.ageInDays; // Oldest first
      } else if (sortBy === 'addedBy') {
        return a.creatorName.localeCompare(b.creatorName);
      }
      return 0;
    });
  }, [topics, sortBy]);

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // If a topic is selected, show topic discussion
  if (selectedTopic) {
    return (
      <TopicDiscussion
        topic={selectedTopic}
        session={session}
        users={users}
        meeting={meeting}
        onClose={() => setSelectedTopic(null)}
        onCloseTopic={handleCloseTopic}
      />
    );
  }

  return (
    <div style={{ padding: '2rem 2.5rem', minHeight: '100vh', background: 'var(--color-bg)' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-text)', marginBottom: '1.25rem' }}>
        {meeting.name} — Topic Discussion
      </h1>
      {closeTopicError && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{closeTopicError}</div>
        )}
        <div className="page-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
            <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
            <Button 
              variant="secondary" 
              size="small" 
              onClick={loadData}
              style={{ marginLeft: '0.5rem' }}
            >
              Refresh Now
            </Button>
          </div>
          <Button variant="danger" onClick={handleEndMeeting}>
            End Meeting
          </Button>
        </div>

        <div className="flex" style={{
          marginBottom: '1rem',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <Button variant="success" onClick={() => setShowTopicModal(true)}>
            Add Topic
          </Button>

          <div className="flex gap-1" style={{ alignItems: 'center' }}>
            <span style={{
              fontSize: '0.9rem',
              fontWeight: '500',
              color: 'var(--color-text-muted)',
              marginRight: '0.5rem'
            }}>
              Sort By:
            </span>
            <Button
              variant={sortBy === 'dateAdded' ? 'primary' : 'secondary'}
              size="small"
              onClick={() => setSortBy('dateAdded')}
            >
              Date Added
            </Button>
            <Button
              variant={sortBy === 'age' ? 'primary' : 'secondary'}
              size="small"
              onClick={() => setSortBy('age')}
            >
              Age
            </Button>
            <Button
              variant={sortBy === 'addedBy' ? 'primary' : 'secondary'}
              size="small"
              onClick={() => setSortBy('addedBy')}
            >
              Added By
            </Button>
          </div>
        </div>

        {sortedTopics.length === 0 ? (
          <EmptyState
            icon="📋"
            title="No open topics"
            message="Add a topic to discuss or end the meeting."
            action={
              <Button variant="secondary" style={{ marginTop: '1rem' }} onClick={() => setShowTopicModal(true)}>
                Add Topic
              </Button>
            }
          />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '50%' }}>Topic</th>
                <th style={{ width: '18%' }}>Date Added</th>
                <th style={{ width: '12%', textAlign: 'center' }}>Age (days)</th>
                <th style={{ width: '20%', textAlign: 'right' }}>Added By</th>
              </tr>
            </thead>
            <tbody>
              {sortedTopics.map(topic => (
                <tr key={topic.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedTopic(topic)}>
                  <td>{topic.title}</td>
                  <td style={{ fontSize: '0.9rem' }}>{formatDate(topic.createdAt)}</td>
                  <td style={{ textAlign: 'center', fontSize: '0.9rem' }}>{topic.ageInDays}</td>
                  <td style={{ textAlign: 'right', fontSize: '0.9rem' }}>{topic.creatorName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      {/* Modal */}
      {showTopicModal && (
        <TopicModal
          meetingId={meeting.id}
          onClose={() => setShowTopicModal(false)}
          onSave={() => {
            setShowTopicModal(false);
            loadData();
          }}
        />
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger !== false}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

ActiveMeetingSession.propTypes = {
  session: PropTypes.object.isRequired,
  meeting: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onEndMeeting: PropTypes.func.isRequired,
};

export default ActiveMeetingSession;
